import { useState } from "react";
import Header from "@/components/landing/header";
import Hero from "@/components/landing/hero";
import Features from "@/components/landing/features";
import CTA from "@/components/landing/cta";
import Footer from "@/components/landing/footer";
// Import Lucide icons
import { 
  Dna, Cpu, Puzzle, Eye, RotateCcw, TrendingUp, Zap, 
  Share2, Lightbulb, Monitor, BarChart, Users, ShieldCheck,
  HardDrive, Search, Handshake
} from "lucide-react";

const whatYouCanDoData = [
  {
    id: 1,
    text: "Analyze single-cell and bulk RNA-seq",
    icon: Dna,
    description: "",
  },
  {
    id: 2,
    text: "Run drug discovery workflows using ML and multi-omics",
    icon: Cpu,
    description: "",
  },
  {
    id: 3,
    text: "Perform spatial-omics analysis across tissue samples",
    icon: Puzzle,
    description: "",
  },
  {
    id: 4,
    text: "Process imaging datasets for biomarker characterization",
    icon: Eye,
    description: "",
  },
  {
    id: 5,
    text: "Integrate RNA, ATAC, proteomics, and methylation data",
    icon: RotateCcw,
    description: "",
  },
  {
    id: 6,
    text: "Build biomarker models from patient data",
    icon: TrendingUp,
    description: "",
  },
  {
    id: 7,
    text: "Automate quality control and variant calling",
    icon: Zap,
    description: "",
  },
  {
    id: 8,
    text: "Store and organize petabytes of data",
    icon: Share2,
    description: "",
  },
  {
    id: 9,
    text: "Customize pipelines for any study",
    icon: Lightbulb,
    description: "",
  },
  {
    id: 10,
    text: "Train AI or ML models using real biological data",
    icon: Cpu,
    description: "",
  },
];

const enterpriseComputeData = [
  {
    id: 1,
    text: "NVIDIA HGX H100 nodes",
    icon: Monitor,
    description: "",
  },
  {
    id: 2,
    text: "8x H100 GPUs, 640 GB HBM3 memory",
    icon: BarChart,
    description: "",
  },
  {
    id: 3,
    text: "3200 Gbps Infiniband networking",
    icon: Zap,
    description: "",
  },
  {
    id: 4,
    text: "PCIe Gen5, NVLink, NVSwitch",
    icon: Puzzle,
    description: "",
  },
  {
    id: 5,
    text: "Up to 100 TB RAM",
    icon: Dna,
    description: "",
  },
  {
    id: 6,
    text: "On-demand, pay-as-you-go",
    icon: TrendingUp,
    description: "",
  },
  {
    id: 7,
    text: "No contracts or reservations required",
    icon: Handshake,
    description: "",
  },
];

const genomicsPipelinesData = [
  {
    id: 1,
    text: "Quality control and filtering",
    icon: Zap,
    description: "",
  },
  {
    id: 2,
    text: "Alignment, clustering, annotation",
    icon: Puzzle,
    description: "",
  },
  {
    id: 3,
    text: "Variant calling and enrichment",
    icon: Dna,
    description: "",
  },
  {
    id: 4,
    text: "Single-cell and spatial workflows",
    icon: RotateCcw,
    description: "",
  },
  {
    id: 5,
    text: "Visual reports and exportable data",
    icon: BarChart,
    description: "",
  },
  {
    id: 6,
    text: "Custom pipelines available",
    icon: Lightbulb,
    description: "",
  },
];

const secureStorageData = [
  {
    id: 1,
    text: "End-to-end encryption",
    icon: ShieldCheck,
    description: "",
  },
  {
    id: 2,
    text: "HIPAA and GDPR compliant",
    icon: Users,
    description: "",
  },
  {
    id: 3,
    text: "Cold and hot storage",
    icon: HardDrive,
    description: "",
  },
  {
    id: 4,
    text: "Full backups and recovery",
    icon: Share2,
    description: "",
  },
  {
    id: 5,
    text: "Metadata search and access control",
    icon: Search,
    description: "",
  },
];

const caseExamples = [
  {
    title: "Brain Aging and Neuroinflammation",
    description:
      "Multi-omics characterization combining RNA-seq, ATAC-seq, and methylation to profile age-linked inflammation markers.",
    icon: Dna,
  },
  {
    title: "T Cell Atlas Development",
    description:
      "Mapped specific T cell populations using single-cell sequencing and immunological analysis.",
    icon: Users,
  },
  {
    title: "AI for Drug Discovery",
    description:
      "Used integrated omics and machine learning to identify potential targets for age-related diseases.",
    icon: Cpu,
  },
  {
    title: "CRISPR Screens on iPSCs",
    description:
      "Performed genome-wide knockout studies in induced pluripotent stem cells linked to aging research.",
    icon: Puzzle,
  },
  {
    title: "Epigenetic Atlas Construction",
    description:
      "Built a high-resolution epigenetic reference across multiple tissues and developmental stages.",
    icon: BarChart,
  },
  {
    title: "Spatial Transcriptomics",
    description:
      "Combined RNA-seq and ATAC-seq to study tissue-specific expression patterns in spatial context.",
    icon: RotateCcw,
  },
  {
    title: "Patient Biomarker Modeling",
    description:
      "Modeled treatment response using patient omics data across conditions like inflammation and immune dysregulation.",
    icon: TrendingUp,
  },
  {
    title: "Foundation Model Encoding",
    description:
      "Developed encoder pipelines to train models that can query heterogeneous omics datasets.",
    icon: Lightbulb,
  },
];

const DiscoveryCallButton = () => {
  return (
    <a 
      href="https://calendly.com/filipp-alchemy/30min"
      target="_blank"
      rel="noopener noreferrer"
      className="bg-gradient-to-r from-[#569EFD] to-[#254cf6] text-white py-4 px-8 rounded-lg border-none font-medium cursor-pointer transition-all duration-200 relative overflow-hidden hover:transform hover:-translate-y-0.5 hover:shadow-lg hover:text-white inline-block"
    >
      Book a Discovery Call
    </a>
  );
};

const Biotech = () => {
  // Accordion state for case examples
  const [openCaseIdx, setOpenCaseIdx] = useState(null);

  return (
    <div className="app">
      <Header />

      {/* Hero Section */}
      <Hero
        title="Cloud compute and genomics workflows designed for biotech teams"
        subtitle="Run analysis, manage data, and train AI models. All in one secure and scalable platform built for science."
        showSearch={false}
        ctaButton={
            <DiscoveryCallButton />
        }
      />

      {/* What You Can Do Section */}
      <section className="features py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-3">One platform, multiple scientific workflows</h2>
          <p className="text-xl text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Support for AI, omics, clinical studies, and drug discovery. No complex setup required.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {whatYouCanDoData.map((item) => (
              <div 
                key={item.id}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200 flex flex-col"
              >
                <div className="mb-4">
                  <item.icon size={32} color="#569EFD" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.text}</h3>
                {item.description && (
                  <p className="text-gray-600 text-sm">{item.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Examples Section - Accordion */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">We help solve the hard problems</h2>
          <p className="text-lg text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            These are examples of real-world challenges we're equipped to tackle. 
            We'll be adding full case studies soon.
          </p>
          <div className="max-w-[800px] mx-auto">
            {caseExamples.map((ex, idx) => (
              <div
                key={idx}
                className={`border border-gray-200 rounded-xl mb-4 bg-white overflow-hidden transition-all duration-200 ${
                  openCaseIdx === idx ? "shadow-lg" : "hover:shadow-sm"
                }`}
              >
                <button
                  onClick={() => setOpenCaseIdx(openCaseIdx === idx ? null : idx)}
                  className="w-full text-left bg-transparent border-none outline-none py-5 px-6 text-lg font-semibold cursor-pointer flex items-center justify-between"
                  aria-expanded={openCaseIdx === idx}
                >
                  <span className="flex items-center gap-4">
                    <span className="flex-shrink-0 bg-blue-50 p-2 rounded-lg">
                      <ex.icon size={24} color="#3B82F6" />
                    </span>
                    <span className="text-gray-800 font-medium text-lg">
                      {ex.title}
                    </span>
                  </span>
                  <span
                    className={`flex-shrink-0 ml-4 inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 transition-transform duration-200 ${
                      openCaseIdx === idx ? "rotate-90" : "rotate-0"
                    }`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 4L10 8L6 12"
                        stroke="#3B82F6"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
                <div
                  className={`overflow-hidden bg-gray-50 transition-all duration-300 ease-in-out`}
                  style={{
                    maxHeight: openCaseIdx === idx ? "200px" : "0px",
                  }}
                >
                  <div
                    className={`p-6 transition-opacity duration-200 ${
                      openCaseIdx === idx ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <p className="text-gray-700 leading-relaxed">{ex.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built on Enterprise Compute Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Backed by industry-leading infrastructure</h2>
          <div className="flex flex-wrap gap-3 justify-center max-w-5xl mx-auto">
            {enterpriseComputeData.map((item) => (
              <div
                key={item.id}
                className="inline-flex items-center bg-white text-gray-800 rounded-full py-3 px-6 border border-blue-400 shadow-sm hover:shadow-md transition-all duration-200 gap-3"
              >
                <span className="text-blue-500">
                  <item.icon size={20} strokeWidth={2} />
                </span>
                <span className="font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Genomics and Multi-Omics Pipelines Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">From raw files to results in hours</h2>
          <p className="text-lg text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Upload your FASTQ, BAM, or h5ad files. We'll take care of the analysis.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {genomicsPipelinesData.map((item) => (
              <div 
                key={item.id}
                className="bg-white border border-gray-200 rounded-lg p-8 hover:shadow-md transition-shadow duration-200 flex flex-col items-center text-center"
              >
                <div className="mb-4 bg-blue-50 p-3 rounded-xl">
                  <item.icon size={28} color="#3B82F6" />
                </div>
                <h3 className="text-lg font-semibold mb-1">{item.text}</h3>
                {item.description && (
                  <p className="text-gray-600 text-sm mt-2">{item.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Secure Storage and Compliance Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Your data is always protected</h2>
          <div className="flex flex-wrap gap-3 justify-center max-w-5xl mx-auto">
            {secureStorageData.map((item) => (
              <div
                key={item.id}
                className="inline-flex items-center bg-white text-gray-800 rounded-full py-3 px-6 border border-blue-400 shadow-sm hover:shadow-md transition-all duration-200 gap-3"
              >
                <span className="text-blue-500">
                  <item.icon size={20} strokeWidth={2} />
                </span>
                <span className="font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <CTA
        title="Let's talk about your research goals"
        description="We'll help you accelerate your analysis, scale your infrastructure, or customize your workflow."
        buttonText={
          <DiscoveryCallButton />
        }
      />

      <Footer />
    </div>
  );
};

export default Biotech;
